// Modale embedded vanilla JS

import {I18nStrings} from '../../i18n';
import {CardFace} from '../../types/flipcardTypes';
import {createModalTemplate} from './template';
import {embeddedModalStyles} from './styles';
import flipcardImageUtils from "@/components/tinyMce/plugins/flipcard/flipcardImageUtils";

export class EmbeddedModal {
  private readonly editor: any;
  private readonly i18n: I18nStrings;
  private modalElement: HTMLElement | null = null;
  private styleElement: HTMLStyleElement | null = null;
  private currentImage: File | null = null;
  private currentImageUrl: string | null = null;
  private currentImageBlobUrl: string = '';

  constructor(editor: any, i18n: I18nStrings) {
    this.editor = editor;
    this.i18n = i18n;
    this.injectStyles();
  }

  private injectStyles(): void {
    if (!this.styleElement) {
      this.styleElement = document.createElement('style');
      this.styleElement.textContent = embeddedModalStyles;
      document.head.appendChild(this.styleElement);
    }
  }

  public open(imageUploadUrl: string): Promise<string | null> {
    return new Promise((resolve) => {
      this.createModal();
      this.attachEventListeners(imageUploadUrl, resolve);
      document.body.appendChild(this.modalElement!);
    });
  }

  private createModal(): void {
    const template = createModalTemplate(this.i18n);
    const container = document.createElement('div');
    container.innerHTML = template;
    this.modalElement = container.firstElementChild as HTMLElement;
  }

  private attachEventListeners(imageUploadUrl: string, resolve: (value: string | null) => void): void {
    if (!this.modalElement) return;

    // Bouton fermer (X)
    const closeBtn = this.modalElement.querySelector('[data-action="close"]');
    closeBtn?.addEventListener('click', () => {
      this.close();
      resolve(null);
    });

    // Bouton annuler
    const cancelBtn = this.modalElement.querySelector('[data-action="cancel"]');
    cancelBtn?.addEventListener('click', () => {
      this.close();
      resolve(null);
    });

    // Bouton soumettre
    const submitBtn = this.modalElement.querySelector('[data-action="submit"]');
    submitBtn?.addEventListener('click', async () => {
      if (this.currentImage) {
        try {
          this.currentImageUrl = await flipcardImageUtils.uploadImage(this.currentImage, imageUploadUrl);
        } catch (error) {
          console.log('An error has occurred.', error);
          this.editor.dispatch('flipCardImageUploadApiError');
        } finally {
          const url = this.currentImageUrl;
          this.close();
          resolve(url);
        }
      }
    });

    // Champ fichier
    const fileInput = this.modalElement.querySelector('[data-field="file"]') as HTMLInputElement;
    fileInput?.addEventListener('change', (e) => {
      this.handleFileInput((e.target as HTMLInputElement).files);
    });

    // Drag and drop
    const fileLabel = this.modalElement.querySelector('.flipcard-modal-file-input');

    fileLabel?.addEventListener('dragover', (e) => {
      e.preventDefault();
      fileLabel.classList.add('drag-over');
    });

    fileLabel?.addEventListener('dragleave', () => {
      fileLabel.classList.remove('drag-over');
    });

    fileLabel?.addEventListener('drop', (e) => {
      e.preventDefault();
      fileLabel.classList.remove('drag-over');
      const files = (e as DragEvent).dataTransfer?.files;
      if (files && files.length > 0) {
        this.handleFileInput(files);
      }
    });

    // Fermer avec Escape
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close();
        resolve(null);
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);

    // Fermer en cliquant sur l'overlay
    const overlay = this.modalElement.querySelector('.flipcard-modal-overlay');
    overlay?.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this.close();
        resolve(null);
      }
    });
  }

  private async handleFileInput(files: FileList | null): Promise<void> {
    const submitBtn = this.modalElement?.querySelector('[data-action="submit"]') as HTMLButtonElement;
    const preview = this.modalElement?.querySelector('[data-preview]') as HTMLElement;
    const previewImg = preview?.querySelector('img');
    const errorElement = this.modalElement?.querySelector('[data-error="file"]') as HTMLElement;
    const fileLabel = this.modalElement?.querySelector('[data-text="file-label"]') as HTMLElement;

    if (files && files.length > 0) {
      const file = files[0];

      // Vérifier si c'est une image
      if (!file.type.startsWith('image/')) {
        errorElement.textContent = 'error';
        errorElement.classList.add('visible');
        preview.classList.remove('visible');
        submitBtn.disabled = true;
        this.currentImageUrl = null;
        return;
      }

      // Vérifier la taille (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        errorElement.textContent = 'error';
        errorElement.classList.add('visible');
        preview.classList.remove('visible');
        submitBtn.disabled = true;
        this.currentImageUrl = null;
        return;
      }

      errorElement.classList.remove('visible');

      try {
        this.currentImage = file;
        this.currentImageBlobUrl = URL.createObjectURL(file);
        submitBtn.disabled = false;

        // Afficher la preview
        if (previewImg) {
          previewImg.src = this.currentImageBlobUrl;
          preview.classList.add('visible');
        }

        // Mettre à jour le label
        if (fileLabel) {
          fileLabel.textContent = file.name;
        }
      } catch (e) {
        errorElement.textContent = 'error';
        errorElement.classList.add('visible');
        preview.classList.remove('visible');
        submitBtn.disabled = true;
        this.currentImageUrl = null;
        console.log('An error has occurred.', e);
        this.editor.dispatch('flipCardImageHandleError');
      }
    }
  }

  private close(): void {
    if (this.currentImageBlobUrl) {
      URL.revokeObjectURL(this.currentImageBlobUrl);
    }
    if (this.modalElement) {
      this.modalElement.remove();
      this.modalElement = null;
    }
    this.currentImageUrl = null;
  }

  public async handleImageInsertion(cardId: string, face: CardFace, imageUploadUrl: string): Promise<void> {
    const imageUrl = await this.open(imageUploadUrl);
    if (imageUrl) {
      this.editor.execCommand('flipcardInsertImage', false, {
        cardId,
        face,
        imageUrl
      });
    }
  }

  public destroy(): void {
    this.close();
    if (this.styleElement) {
      this.styleElement.remove();
      this.styleElement = null;
    }
  }
}
